import type { Profile } from '@/hooks/useProfile'
import ImageUploadBox from '@/components/ui/ImageUploadBox'
import { STORE_FOCUS_OPTIONS } from '@/lib/games'
import { SOCIAL_FIELDS, type SocialKey } from '@/lib/socials'
import { getStoreUrlPreview } from '@/lib/storeSlug'
import {
  BACKGROUND_IMAGE_HINT,
  BANNER_IMAGE_HINT,
  COLOR_SCHEMES,
  STORE_LAYOUTS,
  getColorScheme,
  getColorSchemeOption,
  getStoreLayout,
  type ColorScheme,
  type StoreLayout,
} from '@/lib/storeTheme'
import {Loader2, X} from 'lucide-react'
import { useEffect, useState } from 'react'

interface ProfileFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Profile>) => Promise<void>
  initial?: Profile | null
}

function ProfileFormModal({ open, onClose, onSubmit, initial }: ProfileFormModalProps) {
  const [storeName, setStoreName] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [bannerUrl, setBannerUrl] = useState('')
  const [backgroundUrl, setBackgroundUrl] = useState('')
  const [storeLayout, setStoreLayout] = useState<StoreLayout>('classic')
  const [colorScheme, setColorScheme] = useState<ColorScheme>('pokemon')
  const [favoriteGames, setFavoriteGames] = useState<string[]>([])
  const [tcgplayerStoreUrl, setTcgplayerStoreUrl] = useState('')
  const [ebayStoreUrl, setEbayStoreUrl] = useState('')
  const [socials, setSocials] = useState<Record<SocialKey, string>>({
    tiktokUrl: '',
    xUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    websiteUrl: '',
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setStoreName(initial?.storeName || '')
    setBio(initial?.bio || '')
    setAvatarUrl(initial?.avatarUrl || '')
    setBannerUrl(initial?.bannerUrl || '')
    setBackgroundUrl(initial?.backgroundUrl || '')
    setStoreLayout(getStoreLayout(initial?.storeLayout))
    setColorScheme(getColorScheme(initial?.colorScheme))
    setFavoriteGames(initial?.favoriteGames || [])
    setTcgplayerStoreUrl(initial?.tcgplayerStoreUrl || '')
    setEbayStoreUrl(initial?.ebayStoreUrl || '')
    setSocials({
      tiktokUrl: initial?.tiktokUrl || '',
      xUrl: initial?.xUrl || '',
      facebookUrl: initial?.facebookUrl || '',
      instagramUrl: initial?.instagramUrl || '',
      websiteUrl: initial?.websiteUrl || '',
    })
  }, [open, initial])

  if (!open) return null

  const toggleGame = (game: string) => {
    setFavoriteGames(prev => (prev.includes(game) ? prev.filter(g => g !== game) : [...prev, game]))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!storeName.trim()) return
    setSaving(true)
    try {
      await onSubmit({
        storeName: storeName.trim(),
        bio: bio.trim(),
        avatarUrl,
        bannerUrl,
        backgroundUrl,
        storeLayout,
        colorScheme,
        favoriteGames,
        tcgplayerStoreUrl: tcgplayerStoreUrl.trim(),
        ebayStoreUrl: ebayStoreUrl.trim(),
        tiktokUrl: socials.tiktokUrl.trim(),
        xUrl: socials.xUrl.trim(),
        facebookUrl: socials.facebookUrl.trim(),
        instagramUrl: socials.instagramUrl.trim(),
        websiteUrl: socials.websiteUrl.trim(),
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/80 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-8">
      <div className="relative mb-16 max-h-[calc(100dvh-6rem)] w-full max-w-lg overflow-y-auto rounded-[32px] border-4 border-[#3B4CCA] bg-[#111111] p-5 shadow-[10px_10px_0_rgba(255,26,26,0.22)] sm:mb-0 sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-white/50 hover:bg-white/5 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="font-display mb-5 text-2xl font-extrabold text-white">
          {initial ? 'Edit your store' : 'Set up your store'}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <ImageUploadBox
            label="Banner"
            hint={BANNER_IMAGE_HINT}
            shape="wide"
            value={bannerUrl}
            onChange={setBannerUrl}
          />

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <ImageUploadBox
              label="Avatar"
              shape="round"
              className="w-20 shrink-0"
              value={avatarUrl}
              onChange={setAvatarUrl}
            />
            <div className="min-w-0 flex-1">
              <label className="mb-1.5 block text-xs font-semibold text-white/60">Username / shop name</label>
              <input
                type="text"
                required
                value={storeName}
                onChange={e => setStoreName(e.target.value)}
                placeholder="e.g. MyStoreName"
                className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
              />
              <p className="mt-1.5 text-[11px] font-semibold text-[#FFDE00]">
                {getStoreUrlPreview(storeName || 'MyStoreName', initial?.storeSlug)}
              </p>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">Bio</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder="Tell collectors what you're all about"
              rows={2}
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20 resize-none"
            />
          </div>

          <div className="rounded-3xl border-2 border-white/10 bg-white/[0.03] p-3.5">
            <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/45">Store look</p>
            <p className="mt-1 text-[11px] font-semibold text-white/40">
              These choices change your public store page, not just this editor.
            </p>

            <div className="mt-4">
              <ImageUploadBox
                label="Page background"
                hint={BACKGROUND_IMAGE_HINT}
                shape="hero"
                value={backgroundUrl}
                onChange={setBackgroundUrl}
              />
            </div>

            <div className="mt-4">
              <label className="mb-1.5 block text-xs font-semibold text-white/60">Layout</label>
              <div className="grid gap-2 sm:grid-cols-2">
                {STORE_LAYOUTS.map(option => {
                  const selected = storeLayout === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStoreLayout(option.value)}
                      className={`rounded-2xl border px-3 py-2.5 text-left transition-colors ${
                        selected
                          ? 'border-[#FFDE00] bg-[#FFDE00]/15 text-white'
                          : 'border-white/10 bg-black/20 text-white/70 hover:border-white/25 hover:text-white'
                      }`}
                    >
                      <span className="block text-sm font-extrabold">{option.label}</span>
                      <span className="mt-1 block text-[11px] font-semibold leading-relaxed text-white/45">{option.description}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1.5 block text-xs font-semibold text-white/60">Color scheme</label>
              <div className="relative">
                <select
                  value={colorScheme}
                  onChange={e => setColorScheme(getColorScheme(e.target.value))}
                  className="w-full appearance-none rounded-2xl border-2 border-white/10 bg-black/30 px-3 py-2.5 pr-10 text-sm font-extrabold text-white outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
                >
                  {COLOR_SCHEMES.map(option => (
                    <option key={option.value} value={option.value} className="bg-[#111111] text-white">
                      {option.label} — {option.description}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full border border-black/20" style={{ backgroundColor: getColorSchemeOption(colorScheme).theme.primary }} />
                  <span className="h-3 w-3 rounded-full border border-black/20" style={{ backgroundColor: getColorSchemeOption(colorScheme).theme.secondary }} />
                  <span className="h-3 w-3 rounded-full border border-black/20" style={{ backgroundColor: getColorSchemeOption(colorScheme).theme.accent }} />
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">What do you sell?</label>
            <div className="flex flex-wrap gap-1.5">
              {STORE_FOCUS_OPTIONS.map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGame(g)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    favoriteGames.includes(g)
                      ? 'border-[#FFDE00] bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800]'
                      : 'border-white/15 bg-white/[0.03] text-white/50 hover:border-white/30 hover:text-white'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">TCGplayer store link</label>
            <input
              type="url"
              value={tcgplayerStoreUrl}
              onChange={e => setTcgplayerStoreUrl(e.target.value)}
              placeholder="https://www.tcgplayer.com/..."
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">eBay store link</label>
            <input
              type="url"
              value={ebayStoreUrl}
              onChange={e => setEbayStoreUrl(e.target.value)}
              placeholder="https://www.ebay.com/str/..."
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-white/60">Socials</label>
            <div className="flex flex-col gap-3">
              {SOCIAL_FIELDS.map(field => (
                <div key={field.key}>
                  <label className="mb-1.5 block text-xs font-semibold text-white/60">{field.label}</label>
                  <input
                    type="url"
                    value={socials[field.key]}
                    onChange={e => setSocials(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || !storeName.trim()}
            className="mt-1 flex items-center justify-center gap-2 rounded-full bg-[#FFDE00] py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {initial ? 'Save store' : 'Launch store'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ProfileFormModal
